/**
 * Lazy pane wrapper around <LivePreviewTab />.
 *
 * Loads the transaction model for (project, objName) and renders the existing
 * LivePreviewTab. Used as a sub-tab inside the Prototype screen — no router
 * params, no page chrome (parent provides them).
 */

import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { useAgentRefresh } from '@/hooks/useAgentRefresh'
import LivePreviewTab from '@/livepreview/LivePreviewTab'
import FlowRunPane from '@/pages/designers/scheduler/FlowRunPane'
import type { OpenActionRequest, OpenScreenPayload, WizardActionRequest } from '@twasta/page-runtime/txn'
import { loadTransactionObject } from '@/api/designers'
import { modelOverlayGet } from '@/livepreview/api'
import type {
  TransactionModel,
  TransactionRoot,
} from '@/pages/designers/transaction/types'

function getDefaultModel(name: string): TransactionModel {
  return {
    obj_name: name,
    sql_models: [
      {
        sql_model: {
          form_title: 'Main Form',
          form_descr: '',
          form_no: '1',
          main_table: name.toUpperCase(),
          columns: [],
          actions: [],
          validations: [],
          joins: { join_predicates: { joins: [] } },
        },
      },
    ],
    actions: [],
    cross_updates: [],
    follow_up_actions: [],
    attach_docs: [],
  }
}

interface Props {
  project: string
  objName: string
  /** Deep-link state for the run view: open a record on mount. */
  initialScreen?: { mode?: string; rec?: string; view?: string
    seed?: Record<string, unknown>
    /** A handler='open' action button opened this screen (see TxnScreen). */
    open?: OpenScreenPayload; openKey?: string }
  /** Reflect the open record back to the URL (run view only). */
  onScreenChange?: (s: { mode?: string; rec?: string } | null) => void
  /** 'design' (default, designer preview) bakes Apply-layout into the shared
   *  JSON; 'customize' (run app) stores it as a per-tenant overlay. */
  layoutMode?: 'design' | 'customize'
  /** Offer "Assign to…" on a record. Only true where the surface HAS a work
   *  panel for the task to land in — the Run app, not the designer preview. */
  canAssignWork?: boolean
  /** Open a governed document's filed copy in its repository — passed through
   *  to the record's Document view. Run shell only; the designer preview has
   *  nowhere to navigate to. */
  onOpenFiled?: (args: { repositoryObj: string; documentId: string }) => void
  /** A handler='open' action button asked to NAVIGATE to another object —
   *  the Run shell owns navigation. Absent (designer preview): the target
   *  opens in a drawer instead. */
  onOpenObject?: (req: OpenActionRequest) => void
  /** A handler='wizard' action button asked to NAVIGATE to its wizard — the
   *  Run shell owns navigation. Absent: the wizard opens in a drawer. */
  onOpenWizard?: (req: WizardActionRequest) => void
  /** The record editor opened / closed (a drawer closes with it). */
  onEditorChange?: (s: { open: boolean; txnId: string | null }) => void
  /** Make changes changed the model on disk (see LivePreviewTab). */
  onBuildChange?: () => void
  /** The host's own Make changes button (see LivePreviewTab). */
  makeChangesRequest?: number
  onMakeChangesOpened?: () => void
}

export default function LivePreviewLazyPane({
  project, objName, initialScreen, onScreenChange, layoutMode = 'design',
  canAssignWork = false, onOpenFiled, onOpenObject, onOpenWizard, onEditorChange, onBuildChange,
  makeChangesRequest, onMakeChangesOpened,
}: Props) {
  const [model, setModel] = useState<TransactionModel | null>(null)
  const [loading, setLoading] = useState(true)

  // Reload the model when an Ask-AI run changes project files — otherwise a
  // column the agent added stays invisible until a hard browser refresh (the
  // records re-list, but the screen keeps the model it opened with). An open
  // record editor may hold unsaved input, so the reload waits until it closes.
  const [editorOpen, setEditorOpen] = useState(false)
  const agentRefresh = useAgentRefresh(project, editorOpen)
  const handleEditorChange = useCallback((s: { open: boolean; txnId: string | null }) => {
    setEditorOpen(s.open)
    onEditorChange?.(s)
  }, [onEditorChange])

  useEffect(() => {
    if (!project || !objName) return
    let cancelled = false
    setLoading(true)
    setModel(null)

    // In 'customize' (run app) mode load the EFFECTIVE model — the core with
    // this tenant's overlay merged in — so existing customizations show on open.
    // In 'design' mode load the bare core from the designer endpoint.
    const load = layoutMode === 'customize'
      ? modelOverlayGet(project, objName, 'T').then((res) => {
          const eff = (res.effective as unknown as TransactionRoot)?.transaction
          return eff || getDefaultModel(objName)
        })
      : loadTransactionObject(project, objName).then((data) => {
          if (!data?.content) return getDefaultModel(objName)
          const root = data.content as TransactionRoot
          return root.transaction ? root.transaction : (data.content as TransactionModel)
        })

    load
      .then((loaded) => { if (!cancelled) setModel(loaded) })
      .catch(() => {
        if (cancelled) return
        toast.error('Failed to load transaction object')
        setModel(getDefaultModel(objName))
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [project, objName, layoutMode, agentRefresh])

  if (loading || !model) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        <div className="spinner" />
      </div>
    )
  }

  // An IOFlow flow's input form (docs/flow_forms.md) has no records and no
  // table: its preview IS the flow pane — fill it in and run the flow.
  if (model.flow_form?.flow_id) {
    return (
      <div style={{ height: '100%', minHeight: 480, display: 'flex', flexDirection: 'column', padding: 12 }}>
        <FlowRunPane project={project} objName={objName} showDesignLink={false} />
      </div>
    )
  }

  return (
    <div style={{ height: '100%', minHeight: 480, display: 'flex', flexDirection: 'column' }}>
      <LivePreviewTab
        project={project}
        objName={objName}
        model={model}
        initialScreen={initialScreen}
        onScreenChange={onScreenChange}
        layoutMode={layoutMode}
        canAssignWork={canAssignWork}
        onOpenFiled={onOpenFiled}
        onOpenObject={onOpenObject}
        onOpenWizard={onOpenWizard}
        onEditorChange={handleEditorChange}
        onBuildChange={onBuildChange}
        makeChangesRequest={makeChangesRequest}
        onMakeChangesOpened={onMakeChangesOpened}
      />
    </div>
  )
}
